<template>
	<div>
		<Modal v-show="shown"
			   title=""
			   @close="closeModal"
			   :close-on-click-outside="false"
			   :width="1350"
		>
			<template v-slot:body>
				<div class="modal-content modal-content-l modal-content--with-footer">
					<div v-if="rejectionReason" class="error_hint_box base_margin">
						<span class="font_bold">{{ $t("common.hint") }}:&nbsp;</span>
						<span v-html="$t('seller.upload.installmentContract.modal.rejectionReason.note')"></span>
						<br>
						<br>
						<span class="font_bold">{{ $t("common.rejectionReason") }}:</span>
						<br>
						<span class="font_italic" v-html="rejectionReason"></span>
					</div>
					<div class="flex">
						<div class="flex_1">
							<h4><b>{{$t('seller.upload.installmentContract.modal.contractDetails')}}</b></h4>
							<div class="md-layout">
								<div class="md-layout-item flex flex_column">
									<AvailableDebtorSelect
										:value="debtorId"
										:api="loadAvailableDebtors"
										v-on:input="changeDebtor"
										:required="true"
										data-vv-name="debtorData"
										v-validate="modelValidations.debtorData"
										:error="errors.first('debtorData')"
									></AvailableDebtorSelect>

									<DebtorContactSelect
										:value="contactData"
										v-on:input="changeContact"
										:required="true"
										data-vv-name="contactData"
										v-validate="modelValidations.contactData"
										:error="errors.first('contactData')"
									></DebtorContactSelect>

									<div v-if="isIpuAllowed" class="flex flex_column" style="width:100%;">
										<md-checkbox v-model="contractForm.requestObligorConfirmation">
											{{ $t("seller.upload.Request") }}
											<el-tooltip :content="$t('seller.upload.ipuRequest.tooltip')" placement="top">
												<i class="fa fa-info-circle secondary_color" aria-hidden="true"></i>
											</el-tooltip>
										</md-checkbox>
									</div>

									<div class="flex flex_gap">
										<md-field class="flex_2" :class="[{ 'md-error': errors.has('contractNumber') }]">
											<label>{{ $t("seller.upload.installmentContract.contractNumberOrIdentifier") }}</label>
											<md-input v-model="contractForm.contractNumber"
													  required
													  @input="updateReceivablesAfterContractDataChanged"
													  data-vv-name="contractNumber"
													  v-validate="modelValidations.contractNumber"
											></md-input>
											<span v-if="errors.has('contractNumber')"
												  class="validation-error">{{ errors.first("contractNumber") }}</span>
										</md-field>
										<CurrencySelect class="flex_1"
														v-model="contractForm.currency"
														:isError="!contractForm.currency && touched"
														:required="true"
														data-vv-name="currency"
														v-validate="modelValidations.currency"
														:error="errors.first('currency')"
										></CurrencySelect>
									</div>

									<div class="flex flex_gap">
										<DatePicker
											class="flex_2"
											v-model="contractForm.startDate"
											:placeholder="$t('seller.upload.installmentContract.startDate')"
											data-vv-name="startDate"
											v-validate="modelValidations.startDate"
											required
											:error="errors.first('startDate')"
										></DatePicker>
										<CountrySelect
											class="flex_1"
											:labelText="$t('seller.upload.ApplicableLaw')"
											v-model="contractForm.applicableLaw"
											:companyType=enumDef.companyType.DebtorCompany
											:required="true"
											data-vv-name="applicableLaw"
											v-validate="modelValidations.applicableLaw"
											:error="errors.first('applicableLaw')"
											:tooltip-text="$t('seller.upload.applicableLaw.tooltip')"
										></CountrySelect>
									</div>

									<CommonFileUpload
										class="flex_1"
										placeholderKey="seller.upload.installmentContract.contractDocument"
										icon-name="fa-file-pdf-o"
										accept-type=".pdf"
										:value="contractDocumentFile"
										@input="onContractDocumentChanged"
										:uploadFunction="uploadContractDocForEdit"
									></CommonFileUpload>

									<PdfFormDocuments
										class="margin_top"
										ref="documents"
										:validator="$validator"
										:for-installment-contract="true"
									/>

									<h4 class="font_lg font_bold margin_top">{{ $t("seller.upload.salesParameters") }}</h4>
									<AssignmentTypeSelect
										v-model="contractForm.disclosed"
										:receivable-type="contractForm.type"
										:assignment-type-from-dsr="assignmentTypeFromDsr"
										:required="true"
										data-vv-name="disclosed"
										v-validate="modelValidations.disclosed"
										:error="errors.first('disclosed')"
									></AssignmentTypeSelect>
									<RetentionCheck
										v-model="contractForm.sellWithRetention"
										:debtor-id="debtorId"
										:validation-rules="modelValidations.sellWithRetention"
										:validator="$validator"
										@update="updateRetentionCheck"
									/>
									<NoSanctionsCheck
										v-model="contractForm.noSanctionsConfirmation"
										:validation-rules="modelValidations.noSanctionsConfirmation"
										:validator="$validator"
									/>
								</div>
							</div>
						</div>

						<div class="flex_1">
							<h4><b>{{ $t('seller.upload.installmentContract.modal.editRates') }}</b></h4>
							<div class="md-layout-item flex flex_column">
								<form class="flex">
									<DatePicker class="flex_1"
												v-model="newReceivableForm.dueDate"
												:placeholder="$t('common.dueDate')"
												data-vv-name="dueDate"
												v-validate="receivableModelValidations.dueDate"
												required
												:error="errors.first('dueDate')"
									></DatePicker>

									<md-field class="flex_1 margin_l" :class="[{ 'md-error': errors.has('amount') }]">
										<label>{{ $t("common.amount") }}</label>
										<MoneyAmountInput v-model="newReceivableForm.amount"
														  :required="true"
														  data-vv-name="amount"
														  v-validate="receivableModelValidations.amount"
										></MoneyAmountInput>
										<span v-if="errors.has('amount')" class="validation-error">{{ errors.first('amount') }}</span>
									</md-field>
								</form>
								<div class="flex flex_end flex_gap">
									<md-button v-if="!isRateReceivableEditMode"
											   :disabled="!isReceivableFormValid"
											   class="md-primary"
											   @click="addRateReceivable"
									>
										{{ $t("common.add") }}
									</md-button>
									<template v-else>
										<md-button
											:disabled="!isReceivableFormValid"
											class="md-primary"
											@click="editRateReceivable"
										>
											{{ $t("common.save") }}
										</md-button>
										<md-button
											class="md-block"
											@click="exitRateReceivableEditMode"
										>
											{{ $t("installmentContract.button.exitEditMode") }}
										</md-button>
									</template>
								</div>

								<RateReceivableTableHead
									class="margin_top"
									:selected-rows="selectedRateReceivableRows"
									@generate="openAutoRatesModal"
									@delete="confirmDeleteAllSelectedRateReceivables"
								/>
								<ag-item
									:rowData="contractForm.receivables"
									:columnDefs="rateReceivableColumnDefs"
									height-type="matchRows"
									min-height="164px"
									max-height="575px"
									@selectionChanged="onRateReceivableSelectionChanged"
								></ag-item>
							</div>
						</div>
					</div>
				</div>
				<div class="md-layout modal-content-footer">
					<div class="md-layout-item"></div>
					<div class="md-layout-item flex flex_space">
						<md-button class="md-blank" @click="confirmSave">
							{{ $t("common.save") }}
						</md-button>

						<md-button :disabled="!isDeleteAllowed" class="md-block" @click="confirmDelete">
							{{ $t("common.delete") }}
						</md-button>
						<md-button :disabled="!isSubmitAllowed" :class="{'md-primary': isSubmitAllowed}" @click="confirmSubmission">
							{{ $t("common.button.submit") }}
						</md-button>
					</div>
				</div>
			</template>
		</Modal>
		<InstallmentContractAutoGenerateRatesModal v-if="showAutoRatesModal" @generated="updateWithAutoGeneratedRates" @close="closeAutoRatesModal"/>
		<UploadConfirmation ref="confirmation" @confirm="submitInstallmentContract"/>
	</div>
</template>
<script>
import Modal from "@/components/templates/modal/Modal.vue";
import {enumDef} from "@/common/enumDefs";
import {formatAsDateForAPI, formatAsHtml, isValueSet} from "@/common/format";
import AvailableDebtorSelect from "@/components/templates/seller/AvailableDebtorSelect.vue";
import DebtorContactSelect from "@/components/templates/select/DebtorContactSelect.vue";
import {receivableApi} from "@/common/api/receivableApi";
import RetentionCheck from "@/components/templates/seller/RetentionCheck.vue";
import NoSanctionsCheck from "@/components/templates/seller/NoSanctionsCheck.vue";
import TypeSelect from "@/components/templates/select/TypeSelect.vue";
import {mapGetters} from "vuex";
import CurrencySelect from "@/components/templates/select/CurrencySelect.vue";
import DatePicker from "@/components/templates/DatePicker.vue";
import CountrySelect from "@/components/templates/select/CountrySelect.vue";
import PdfFormDocuments from "@/components/templates/seller/PdfFormDocuments.vue";
import ReceivableDocumentFileUpload from "@/components/templates/seller/ReceivableDocumentFileUpload.vue";
import CommonFileUpload from "@/components/templates/common/CommonFileUpload.vue";
import {validateForm, validateRequiredFields} from "@/validation/validation";
import MoneyAmountInput from "@/components/templates/common/MoneyAmountInput.vue";
import AgItem from "@/components/templates/agGrid/AgItem.vue";
import {asLocalizedDate, asMoneyAmount} from "@/components/templates/agGrid/formatter/valueFormatters";
import AssignmentTypeSelect from "@/components/templates/select/AssignmentTypeSelect.vue";
import RateReceivableTableHead from "@/components/templates/seller/sell/RateReceivableTableHead.vue";
import InstallmentContractAutoGenerateRatesModal from "@/components/templates/seller/sell/InstallmentContractAutoGenerateRatesModal.vue";
import UploadConfirmation from "@/components/templates/seller/UploadConfirmation.vue";

export default {
	components: {
		UploadConfirmation,
		RateReceivableTableHead,
		AssignmentTypeSelect,
		AgItem,
		MoneyAmountInput,
		CommonFileUpload,
		ReceivableDocumentFileUpload,
		PdfFormDocuments,
		CountrySelect,
		DatePicker,
		CurrencySelect,
		TypeSelect,
		NoSanctionsCheck, RetentionCheck,
		DebtorContactSelect, AvailableDebtorSelect,
		Modal,
		InstallmentContractAutoGenerateRatesModal
	},
    data() {
		let defaultContractForm = {
			contractDocumentFileId: "",
			contractDocumentFileName: "",
			contractNumber: "",
			startDate: "",
			currency: "",
			applicableLaw: "",
			requestObligorConfirmation: false,
			noSanctionsConfirmation: false,
			sellWithRetention: false,
			type: enumDef.receivableType.InstallmentContractRate,
			disclosed: null,
			offerDurationInHours: null,
			additionalDocuments: [],
			receivables: [],
		};

		let defaultReceivableForm = {
			index: null,
			id: null,
			invoiceNumber: "",
			dueDate: "",
			amount: null,
		};

		return {
			contractId: null,
			enumDef: enumDef,
			shown: false,
			showAutoRatesModal: false,
			rateReceivableEditMode: false,
			rateIndexForEdit: null,
			defaultContractForm: { ...defaultContractForm },
			defaultReceivableForm: { ...defaultReceivableForm },
			newReceivableForm: { ...defaultReceivableForm },
			contractForm: { ...defaultContractForm },
			rateReceivableColumnDefs: [],
			selectedRateReceivableRows: [],
			modelValidations: {
				debtorData: {
					required: true
				},
				contactData: {
					required: true
				},
				currency: {
					required: true
				},
				contractNumber: {
					required: true
				},
				startDate: {
					required: true
				},
				applicableLaw: {
					required: true
				},
				noSanctionsConfirmation: {
					checked: true
				},
				sellWithRetention: {
					checked: true
				},
				disclosed: {
					required: true
				},
			},
			receivableModelValidations: {
				dueDate: {
					required: true
				},
				amount: {
					required: true
				}
			},
			contractInputFields: [
				"debtorData", "contactData", "currency", "disclosed",
				"applicableLaw", "sellWithRetention", "noSanctionsConfirmation"
			],
			additionalContractDocumentFields: [
				"additionalRequiredDocument_.*"
			],
			rateReceivableInputFields: ["dueDate", "amount"],
			retentionCheck: {
				sellWithRetention: false,
				textParams: {
					retentionInPercent: null
				}
			},
			contractChanged: false,
			receivablesLimitForPleaseWaitMessage: 20,
		};
	},
	created() {
		if (!this.uploadSettingsForInstallmentContract) {
			receivableApi.inspection.getUploadSettings({ forInstallment: true })
				.then(res => {
					this.$store.dispatch("setActionUploadSettings", { forInstallmentContract: res });
				})
				.catch(this.toast);
		}

	},
	beforeMount() {
		this.setRateReceivableColumnDefs();
		this.resetForm();
	},
	methods: {
		formatAsHtml,
		resetForm() {
			this.contractForm = { ...this.defaultContractForm };
			this.newReceivableForm = { ...this.defaultReceivableForm };
			this.$store.dispatch("setActionDebtorData", null);
			this.$validator.reset();
			this.contractChanged = false;
		},
		show(value) {
			this.shown = value;
		},
  		closeModal() {
            this.show(false);

			this.$store.dispatch("setActionLastAddedDebtorResult", null);

			if (this.contractChanged) {
				this.$emit("contractChanged");
			}

            this.resetForm();
        },
        openAutoRatesModal() {
            this.showAutoRatesModal = true;
        },
        closeAutoRatesModal() {
            this.showAutoRatesModal = false;
        },
		showModalWithContractId(contractId) {
			this.resetForm();
			this.contractId = contractId;

			receivableApi.installmentContractInspection.getContractDetails(contractId)
				.then(res => {
					this.showModalWithContractData(res);
				})
				.catch(this.toast);
		},
		showModalWithContractData(contractData) {
			this.show(true);
			this.contractId = contractData.contractId;
			this.contractForm = { ...this.defaultContractForm, ...contractData };

			// Make sure the list is properly sorted and indexed
			this.rebuildReceivablesList(contractData.receivables, true);

			this.initializeDisclosedSetting(contractData);
			this.initializeDocuments();
			this.initializeSaleParameters();

			if (contractData.debtor) {
				this.changeDebtor(contractData.debtor);
			} else if (this.lastAddedDebtorResult) {
				this.changeDebtor(this.lastAddedDebtorResult.debtor);
			}

			if (contractData.debtorContact) {
				this.changeContact(contractData.debtorContact);
			} else if (this.lastAddedDebtorResult) {
				this.changeContact(this.lastAddedDebtorResult.contact);
			}

			// Start with receivable edit mode if there is still only the placeholder rate receivable
			const firstReceivable = (this.contractForm.receivables)?.[0];
			if (firstReceivable && !firstReceivable.dueDate) {
				firstReceivable.index = 0;
				this.enterRateReceivableEditMode({ ...this.defaultReceivableForm, ...firstReceivable });
			} else {
				this.exitRateReceivableEditMode();
			}
		},
		loadAvailableDebtors() {
			return receivableApi.availableDebtors();
		},
		updateReceivablesAfterContractDataChanged() {
			this.rebuildReceivablesList(this.contractForm.receivables, false);
		},
		changeDebtor(data) {
			let previousDebtorId = this.debtorId;
			let newDebtorId = data?.id;

			// Always update the data, since it is possible that we only have the ID and only later get all the data.
			// E.g., on initialization only the ID is set at first and then the full data is loaded later by the debtor select component
			this.$store.dispatch("setActionDebtorData", data);

			if (previousDebtorId === newDebtorId) {
				return;
			}

			// Only set applicable law if it is saved in the debtor relation
			if (!this.contractForm.applicableLaw && data?.applicableLaw) {
				this.contractForm.applicableLaw = data.applicableLaw;
			}

			this.updateDisclosedSettingFromPreferences();

			if (this.contactData) {
				this.changeContact(null);
			}
		},
		changeContact(data) {
			this.contactData = data;
			this.$store.dispatch("setActionContactData", data);
		},
		uploadContractDocForEdit(params) {
			return receivableApi.installmentContractOperation.uploadContractDocForEdit({file: params.file});
		},
		onContractDocumentChanged(newDocument) {
			this.contractForm.contractDocumentFileId = newDocument.fileId;
			this.contractForm.contractDocumentFileName = newDocument.fileName;
		},
		initializeDisclosedSetting(contractData) {
			if (!this.contractForm.isFromInitialization) {
				this.contractForm.disclosed = contractData.disclosed;
			} else {
				this.updateDisclosedSettingFromPreferences();
			}
		},
		initializeDocuments() {
			// Only update documents if requirements are already loaded
			if (this.requiredDocumentFileTypes) {
				this.$refs.documents.initializeDocumentsWithRequirements(this.contractForm.additionalDocuments, this.requiredDocumentFileTypes);
			}
		},
		initializeSaleParameters() {
			if (!this.contractForm.offerDurationInHours) {
				this.contractForm.offerDurationInHours = enumDef.SellDurationOptions.properties[enumDef.SellDurationOptions.default].value;
			}
		},
		updateRetentionCheck(retentionCheck) {
			this.retentionCheck = retentionCheck;
		},
		updateDisclosedSettingFromPreferences() {
			// try to find any preselected settings
			if (isValueSet(this.assignmentTypeFromDsr)) {
				// preselect by assignmentType on the debtor level
				this.form.disclosed = this.debtorData.assignmentType;
			} else if (isValueSet(this.disclosedPreference)) {
				this.contractForm.disclosed = this.disclosedPreference;
			} else {
				this.contractForm.disclosed = true;
			}
		},
		buildContractAndReceivablesData() {
			let option = { ...this.contractForm };

			let { requestObligorConfirmation } = this.contractForm;

			option.contractDocumentFileId = this.contractDocumentFile.fileId;
			option.contractDocumentFileName = this.contractDocumentFile.fileName;

			option.debtor = { id: this.debtorId };
			option.debtorContact = this.contactData;
			option.requestObligorConfirmation = requestObligorConfirmation;

			// Only send documents that were actually uploaded
			option.additionalDocuments = this.contractForm.additionalDocuments.filter(file => file.fileId);

			option.receivables = this.contractForm.receivables;
			return option;
		},

		// methods for receivables
		setRateReceivableColumnDefs() {
			this.rateReceivableColumnDefs = [
				{
					headerName: this.$t("installmentContract.rateTable.rateId"),
					minWidth: 160,
					maxWidth: 250,
					field: "invoiceNumber",
					sortable: false,
				},
				{
					headerName: this.$t("common.amount"),
					headerClass: "align-right",
					field: "amount",
					valueFormatter: asMoneyAmount(),
					cellClass: "align-right",
					minWidth: 100,
					maxWidth: 160,
					sortable: false,
				},
				{
					headerName: this.$t("common.dueDate"),
					field: "dueDate",
					minWidth: 100,
					maxWidth: 110,
					valueFormatter: asLocalizedDate(),
					sortable: true,
				},
				{
					headerName: this.$t("common.status"),
					minWidth: 100,
					maxWidth: 120,
					field: "status",
					cellRendererFramework: "EnumAsText",
					enumDef: enumDef.receivableStatus,
					sortable: false,
				},
				{
					headerName: this.$t("common.actions"),
					minWidth: 80,
					maxWidth: 80,
					field: "",
					cellRendererFramework: "RateReceivableTableOperation",
					cellRendererParams: {
						delFun: this.confirmDeleteRateReceivable,
						editFun: this.enterRateReceivableEditMode
					},
					pinned: "right",
				},
				{
					minWidth: 45,
					maxWidth: 45,
					headerCheckboxSelection: true,
					checkboxSelection: true,
					headerCheckboxSelectionFilteredOnly: true,
					pinned: "right",
				}
			];
		},
		onRateReceivableSelectionChanged(data) {
			this.selectedRateReceivableRows = data;
		},
		buildInvoiceNumberForRateReceivableWithDueDate(contractNumber, dueDate) {
			let dueDateString = dueDate ? formatAsDateForAPI(dueDate) : "";
			return contractNumber + '_' + dueDateString;
		},
		updateWithAutoGeneratedRates(receivables) {
			this.rebuildReceivablesList(receivables);

			this.exitRateReceivableEditMode();
			this.selectedRateReceivableRows = [];

			this.closeAutoRatesModal();
		},
		rebuildReceivablesList(updatedReceivableList, skipDataUpdate = false) {
			const sorted = updatedReceivableList.sort((a, b) => {
				const momentA = moment(a.dueDate);
				const momentB = moment(b.dueDate);
				return momentA - momentB;
			});

			sorted.forEach((r, index) => {
				r.index = index;

				if (skipDataUpdate) {
					return;
				}

				if (!r.status) {
					r.status = enumDef.receivableStatus.Draft;
				}

				r.invoiceNumber = this.buildInvoiceNumberForRateReceivableWithDueDate(this.contractForm.contractNumber, r.dueDate);
			});

			this.contractForm.receivables = sorted;
		},
		addRateReceivable(){
			const updatedReceivableList = this.contractForm.receivables.slice();

			// Add new receivable and rebuild the list again
			updatedReceivableList.push({ ...this.newReceivableForm });
			this.rebuildReceivablesList(updatedReceivableList);

			this.exitRateReceivableEditMode(); // Indexes might have changed
		},
		editRateReceivable() {
			const updatedReceivableList = this.contractForm.receivables.slice();

			// Merge changes with the existing receivable to preserve fields like id/status (unless overridden) and rebuild the list again
			updatedReceivableList[this.rateIndexForEdit] = {
				...updatedReceivableList[this.rateIndexForEdit],
				...this.newReceivableForm
			};
			this.rebuildReceivablesList(updatedReceivableList);

			this.exitRateReceivableEditMode();
		},
		deleteRateReceivable(data) {
			this.contractForm.receivables.splice(data.index, 1);
			this.rebuildReceivablesList(this.contractForm.receivables, true);

			this.exitRateReceivableEditMode(); // Indexes might have changed
		},
		deleteAllSelectedRateReceivables() {
			// Delete in descending index order so indices don't shift during deletion
			this.selectedRateReceivableRows
				.map(r => r.index)
				.sort((a, b) => b - a)
				.forEach(index => this.contractForm.receivables.splice(index, 1));
			this.rebuildReceivablesList(this.contractForm.receivables, true);

			this.exitRateReceivableEditMode(); // Indexes might have changed
		},
		enterRateReceivableEditMode(data) {
			this.newReceivableForm.id = data.id;
			this.newReceivableForm.dueDate = data.dueDate;
			this.newReceivableForm.amount = data.amount;
			this.rateReceivableEditMode = true;
			this.rateIndexForEdit = data.index;
		},
		exitRateReceivableEditMode() {
			this.newReceivableForm = { ...this.defaultReceivableForm };
			this.rateReceivableEditMode = false;
			this.rateIndexForEdit = null;
			this.resetRateInputFieldValidation();
		},
		resetRateInputFieldValidation() {
			this.rateReceivableInputFields.forEach(field => this.$validator.reset({ name: field }));
		},
		confirmDeleteRateReceivable(data) {
			let option = {
				title: this.$t("installmentContract.delete.rate.confirm.title"),
				msg: this.$t("installmentContract.delete.rate.confirm.text"),
				confirmFun: this.deleteRateReceivable,
				params: data
			};
			this.confirmMeg(option, true);
		},
		confirmDeleteAllSelectedRateReceivables() {
			let option = {
				title: this.$t("installmentContract.delete.rate.multi.confirm.title"),
				msg: this.$t("installmentContract.delete.rate.multi.confirm.text", {
					deletableCount: this.filterSelectedRateReceivables(this.isDeleteForRateReceivableAllowed).length,
					selectedCount: this.selectedRateReceivableRows.length
				}),
				confirmFun: this.deleteAllSelectedRateReceivables,
			};
			this.confirmMeg(option, true);
		},
		confirmSave() {
			// When in edit mode or there is no valid rate, we should also validate the current rate receivable fields => ignored fields filter = none.
			// Otherwise, we only validate the contract fields => ignored fields filter = rate receivable fields.
			// This is needed because the rate input fields are always displayed as required, so the user can add new ones.
			// Additional contract documents are always optional for saving, so we always ignore them for validation.
			const ignoredFields = [
				...this.additionalContractDocumentFields,
				...(this.rateReceivableEditMode || this.hasInvalidRates ? [] : this.rateReceivableInputFields)
			];
			validateForm(this, this.$validator, ignoredFields)
				.then(() => {
					this.saveInstallmentContract();
				})
				.catch(this.toast);
		},
		saveInstallmentContract(callback) {
			if (this.contractForm.receivables.length > this.receivablesLimitForPleaseWaitMessage) {
				this.toast(this.$t("common.loadingHint.pleaseWait"), "warning");
			}

			let option = this.buildContractAndReceivablesData();
			receivableApi.installmentContractOperation.saveContract(option)
				.then(res => {
					if (res.updatedReceivables) {
						this.rebuildReceivablesList(res.updatedReceivables, true);
						this.contractChanged = true;
					}

					if (res.errors.length > 0) {
						this.toast(res.errors);
						return;
					}

					// If there is a follow-up callback, let them handle the user notification after they are done
					if (callback) {
						callback();
					} else {
						this.toastSuccess();
					}
				})
				.catch(this.toast);
		},
		confirmDelete() {
			let option = {
				title: this.$t("installmentContract.delete.confirm.title"),
				msg: this.$t("installmentContract.delete.confirm.msg"),
				confirmFun: this.deleteInstallmentContract,
			};
			this.confirmMeg(option, true);
		},
		deleteInstallmentContract() {
			receivableApi.installmentContractOperation.deleteContract(this.contractId)
				.then(() => {
					this.toastSuccess();
					this.$emit("contractChanged");
					this.closeModal();
				})
				.catch(this.toast);
		},
		confirmSubmission() {
			// Ignore logic matches with input field validation for saving. See comment there.
			// Except additional contract documents are always required to submit, so we do ignore them for validation here.
			const ignoredFields = this.rateReceivableEditMode || this.hasInvalidRates ? [] : this.rateReceivableInputFields;

			validateForm(this, this.$validator, ignoredFields)
				.then(() => {
					// Save first so we are sure all receivables have ids for submission
					this.saveInstallmentContract(this.showConfirmSubmissionModal);
				})
				.catch(this.toast);
		},
		showConfirmSubmissionModal() {
			let rateReceivableIds = this.contractForm.receivables.map(item => item.id);
			this.$refs.confirmation.showWithReceivableIds(rateReceivableIds);
		},
		submitInstallmentContract() {
			if (this.contractForm.receivables.length > this.receivablesLimitForPleaseWaitMessage) {
				this.toast(this.$t("common.loadingHint.pleaseWait"), "warning");
			}

			receivableApi.installmentContractOperation.submitContract(this.contractId)
				.then(() => {
					this.contractChanged = true;
					this.toastSuccess();
					this.closeModal();
				})
				.catch(this.toast);
		},
		filterSelectedRateReceivables(filterPredicate) {
			return this.selectedRateReceivableRows.filter(filterPredicate);
		},
		isDeleteForRateReceivableAllowed(receivable) {
			// todo installment: add MS condition when MS is allowed
			return enumDef.receivableStatus.isDeletable(receivable.status);
		}
	},
	computed: {
		...mapGetters({
			lastAddedDebtorResult: "lastAddedDebtorResult",
			debtorData: "debtorData",
			contactData: "contactData",
			uploadSettings: "uploadSettings",
		}),
		uploadSettingsForInstallmentContract() {
			return this.uploadSettings.forInstallmentContract;
		},
		isRateReceivableEditMode() {
			return this.rateReceivableEditMode;
		},
		hasInvalidRates() {
			let receivables = this.contractForm.receivables;
			return receivables.length === 0 || receivables.some(r => !r.dueDate || !r.amount);
		},
		isContractFormValid() {
			return validateRequiredFields(this.contractForm, this.modelValidations);
		},
		isReceivableFormValid() {
			return validateRequiredFields(this.newReceivableForm, this.receivableModelValidations);
		},
		changeable() {
			// Same check as for normal receivables, just iterating over all rates
			return this.contractForm.receivables.every(r => {
				if (!isValueSet(r.id) || !isValueSet(r.status)) {
					return true;
				}
				// todo installment: add MS condition when MS is allowed
				return enumDef.receivableStatus.isChangeable(r.status);
			});
		},
		isSaveAllowed() {
			return this.changeable;
		},
		isDeleteAllowed() {
			return this.contractForm.receivables.every(this.isDeleteForRateReceivableAllowed);
		},
		isSubmitAllowed() {
			return this.isContractFormValid && this.changeable && !this.hasInvalidRates;
		},
		debtorId() {
			return this.debtorData ? this.debtorData.id : null;
		},
		isIpuAllowed() {
			return this.contractForm.disclosed && this.debtorData && enumDef.companyStatus.isRegisteredTrader(this.debtorData.status ?? this.debtorData.debtorStatus);
		},
		requiredDocumentFileTypes() {
			return this.uploadSettingsForInstallmentContract ? this.uploadSettingsForInstallmentContract.requiredDocumentFileTypes : [];
		},
		disclosedPreference() {
			return this.$sessionStorage.get("localeUser").auctionDisclosed;
		},
		contractDocumentFile() {
			return {
				fileId: this.contractForm.contractDocumentFileId,
				fileName: this.contractForm.contractDocumentFileName,
			}
		},
		rejectionReason() {
			// Right now, all receivables are rejected together, but only the "main" rejected one has a reason that we display
			let receivableWithRejectionReason = this.contractForm.receivables.find(r => r.rejectionReason);

			return receivableWithRejectionReason? `${receivableWithRejectionReason.invoiceNumber}: ${receivableWithRejectionReason.rejectionReason}` : null;
		},
		assignmentTypeFromDsr() {
			return this.debtorData?.assignmentType;
		}
	}
}
</script>